import { addReport } from '@/api/evaluation'
import {
  deleteList,
  deleteOfflineLogImportRecord,
  detailAttCkList,
  getDefenseRankings,
  getDeviceAlarmStat,
  getKillChain,
  getListDetailsElement,
  getOfflineLogImportInfo,
  getPluginFilter,
  getRequest,
  getResultRiskLevel,
  getRingPattern,
  getTagTreeOptions,
  getTaskInfo,
  resultComponentChart,
  resultDeviceOption,
  resultTemplateDefenseRankings,
  updateResourceInterceptStatus,
  getResourceFilterData,
  getTaskScore,
  getCaseLevelDistribution,
  getRiskLevelDistribution,
  getUseCaseStatusDistribution,
  getAttackProtectionRanking,
  getDeviceProtectionRanking,
  getTemplateProtectionRanking,
  getInterceptStatus,
} from '@/api/threat-list-task'
import { Message } from '@/components/message'
import { useRequest } from 'ahooks'
import { t } from '@/i18n'

// 任务信息 任务进度
export const useGetResultInfo = () => {
  const { runAsync: run, data } = useRequest(params => getTaskInfo(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('任务信息获取失败'))
    },
  })
  return [run, data?.data || []] as const
}

// 获取用例列表
export const useGetUseCaseList = () => {
  const { runAsync: run, loading } = useRequest(params => getListDetailsElement(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取用例列表失败'))
    },
  })

  return [run, loading] as const
}

// 获取攻击类型
export const useGetAttackType = () => {
  const { runAsync: run, data } = useRequest(params => getAttackType(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取攻击类型失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 获取ATT&CK
export const useGetAttck = () => {
  const { runAsync: run, data } = useRequest(params => detailAttCkList(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取att&ck失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 获取关联安全设备
export const useGetDevices = () => {
  const { runAsync: run, data } = useRequest(params => resultDeviceOption(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取关联安全设备失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 获取用例标签列表
export const useGetTagTreeOptions = () => {
  const { runAsync: run, data } = useRequest(params => getTagTreeOptions(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取标签列表失败'))
    },
  })

  return [run, data?.data?.categoryList || []] as const
}

// 更新资源拦截状态
export const useUpdateResourceInterceptStatus = () => {
  const { runAsync: run, loading } = useRequest(params => updateResourceInterceptStatus(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('更新资源拦截状态失败'))
    },
  })

  return [run, loading] as const
}

// 删除任务
export const useDeleteList = () => {
  const { runAsync: run } = useRequest(params => deleteList(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('删除任务失败'))
    },
  })

  return [run] as const
}

// 生成报表
export const useAddReport = () => {
  const { runAsync: run, loading } = useRequest(params => addReport(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('报表添加失败'))
    },
  })

  return [run, loading] as const
}

// 获取离线日志导入信息
export const useGetOfflineLogImportInfo = () => {
  const { runAsync: run, data } = useRequest(params => getOfflineLogImportInfo(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取离线日志导入信息失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 删除日志设备导入记录
export const useDeleteOfflineLogImportInfo = () => {
  const { runAsync: run, loading } = useRequest(params => deleteOfflineLogImportRecord(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('删除日志设备导入记录失败'))
    },
  })

  return [run, loading] as const
}

// 获取攻击防护排名 (轮播)
export const useGetProtectRate = () => {
  const { runAsync: run, data } = useRequest(params => getResultDefenseRankings(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取攻击防护排名失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 获取危险等级分布 (图表)
export const useGetRiskRate = () => {
  const { runAsync: run, data } = useRequest(params => getResultRiskLevel(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取危险等级分布失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 获取模板拦截失败率排名 (图表)
export const useGetTemplateFailureRate = () => {
  const { runAsync: run, data } = useRequest(params => resultTemplateDefenseRankings(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取模板拦截失败率失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 抽屉详情
export const useGetResultPluginDetail = () => {
  const {
    runAsync: run,
    data,
    loading,
  } = useRequest((datas: GetResultPluginDetail) => getResultPluginDetail(datas), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('获取插件详情失败'))
    },
  })
  return [run, data?.data || {}, loading] as const
}

// 获取插件筛选条件
export const useGetPluginFilter = () => {
  const { runAsync: run, data } = useRequest(params => getPluginFilter(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('筛选条件获取失败'))
    },
  })
  return [run, data?.data?.attackTypeList || []]
}

// 获取杀伤链列表
export const useGetKillChain = () => {
  const { runAsync: run, data } = useRequest(params => getKillChain(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取杀伤链失败'))
    },
  })

  return [run, data?.data?.kill_rate || []] as const
}

// 获取任务摘要-任务拦截
export const useGetRingPattern = () => {
  const { runAsync: run, data } = useRequest(params => getRingPattern(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取任务摘要-任务拦截失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 攻击防护排名
export const useGetDefenseRankings = () => {
  const { data, runAsync: run } = useRequest(params => getDefenseRankings(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取攻击防护排名失败'))
    },
  })

  return [run, data?.data?.attack_rate || []] as const
}

// 影响组件分布 (图表)
export const useGetResultComponentChart = () => {
  const { runAsync: run, data } = useRequest(params => resultComponentChart(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取影响组件数据失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 获取设备告警统计
export const useGetDeviceAlarmStat = () => {
  const { runAsync: run, data } = useRequest(params => getDeviceAlarmStat(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取设备告警统计失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 插件列表详情
export const useGetPluginDetail = () => {
  const {
    runAsync: run,
    data,
    loading,
  } = useRequest(params => getRequest(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || t('插件列表详情获取失败'))
    },
  })

  return [run, data?.data || {}, loading] as const
}

// 2.0.2
// 获取筛选聚合
export const useGetResourceFilterData = () => {
  const { runAsync: run, data } = useRequest(params => getResourceFilterData(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取数据失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 获取任务分数
export const useGetTaskScore = () => {
  const { runAsync: run, data } = useRequest(params => getTaskScore(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取任务分数失败'))
    },
  })

  return [run, data?.data || ''] as const
}

// 用例分级分布
export const useGetCaseLevelDistribution = () => {
  const { runAsync: run, data } = useRequest(params => getCaseLevelDistribution(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取用例分级分布失败'))
    },
  })

  return [run, data?.data?.list || []] as const
}

// 获取风险等级分布
export const useGetRiskLevelDistribution = () => {
  const { runAsync: run, data } = useRequest(params => getRiskLevelDistribution(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取风险等级分布失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 用例状态分布
export const useGetUseCaseStatusDistribution = () => {
  const { runAsync: run, data } = useRequest(params => getUseCaseStatusDistribution(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取用例状态分布失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 攻击防护排名
export const useGetAttackProtectionRanking = () => {
  const { runAsync: run, data } = useRequest(params => getAttackProtectionRanking(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取攻击防护排名失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}

// 设备防护排名
export const useGetDeviceProtectionRanking = () => {
  const { runAsync: run, data } = useRequest(params => getDeviceProtectionRanking(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取设备防护排名失败'))
    },
  })

  return [run, data?.data?.devices || []] as const
}

// 模板防护排名
export const useGetTemplateProtectionRanking = () => {
  const { runAsync: run, data } = useRequest(params => getTemplateProtectionRanking(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取模板防护排名失败'))
    },
  })

  return [run, data?.data?.list || []] as const
}

// 获取拦截状态
export const useGetInterceptStatus = () => {
  const { runAsync: run, data } = useRequest(params => getInterceptStatus(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取拦截状态失败'))
    },
  })

  return [run, data?.data?.items || []] as const
}
